import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Box,
  Typography,
  Stack,
  Grid,
  Paper,
  Chip,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  LinearProgress,
  Alert,
  IconButton,
  Divider,
  CircularProgress,
  useMediaQuery,
  Avatar,
  Collapse,
  Fade,
  TableContainer,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  AttachMoney as MoneyIcon,
  Close as CloseIcon,
  AccountBalanceWallet as WalletIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Business as BusinessIcon,
  Percent as PercentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTheme, alpha, styled } from '@mui/material/styles';
import client from '../../API';

/** Helper functions */
const fmtMoney = (v) => `$${Number(v || 0).toFixed(2)}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { 
  month: 'short', day: 'numeric', year: 'numeric' 
}) : '—';

// Mobile safe area constants
const APP_SHELL_HEIGHT = 86;
const EXTRA_GUTTER = 12;
const SAFE_AREA_BOTTOM = 'env(safe-area-inset-bottom, 0px)';

// Collapsible Section Component
const CollapsibleSection = ({ title, icon, children, defaultExpanded = true, isMobile }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  
  return (
    <Paper sx={{ mb: 2, overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: isMobile ? 1.5 : 2,
          bgcolor: 'grey.50',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'grey.100' },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {React.cloneElement(icon, { sx: { fontSize: isMobile ? 18 : 24 } })}
          <Typography 
            variant={isMobile ? "body2" : "subtitle1"} 
            fontWeight={600}
          >
            {title}
          </Typography>
        </Box>
        {expanded ? <ExpandLessIcon sx={{ fontSize: isMobile ? 18 : 24 }} /> : <ExpandMoreIcon sx={{ fontSize: isMobile ? 18 : 24 }} />}
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ p: isMobile ? 1.5 : 2 }}>
          {children}
        </Box>
      </Collapse>
    </Paper>
  );
};

// Info Row Component
const InfoRow = ({ label, value, icon, highlight = false, isMobile }) => (
  <TableRow sx={{ '&:last-child td': { border: 0 } }}>
    <TableCell 
      sx={{ 
        fontWeight: 600, 
        color: 'text.secondary', 
        width: '40%', 
        py: isMobile ? 1 : 1.5,
        fontSize: isMobile ? '0.75rem' : '0.875rem',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {React.cloneElement(icon, { sx: { fontSize: isMobile ? 16 : 20 } })}
        {label}
      </Box>
    </TableCell>
    <TableCell sx={{ py: isMobile ? 1 : 1.5 }}>
      {highlight ? (
        <Typography 
          variant="body2" 
          fontWeight={700} 
          color="primary.main"
          sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
        >
          {value}
        </Typography>
      ) : (
        <Typography 
          variant="body2"
          sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
        >
          {value}
        </Typography>
      )}
    </TableCell>
  </TableRow>
);

/**
 * ReferralDetailDialog Component
 * Full-screen optimized with desktop two-column layout
 */
export default function ReferralDetailDialog({ open, onClose, referralId }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery('(max-width:400px)');
  
  const queryClient = useQueryClient();
  const [claimingIds, setClaimingIds] = useState(new Set());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Fetch referral details
  const { data, isLoading, isError, refetch } = useQuery(
    ['referral-detail', referralId],
    () => client.globalUsers.getReferralDetailById(referralId, { include_access: true }),
    { 
      enabled: !!referralId && open,
      staleTime: 1000 * 30,
    }
  );

  // Fetch eligible commissions
  const { data: eligibleData, refetch: refetchEligible, isLoading: eligibleLoading } = useQuery(
    ['referral-eligible-commissions', referralId],
    () => client.commissions.getReferralEligibleCommissions(referralId),
    {
      enabled: !!referralId && open,
      staleTime: 1000 * 30,
    }
  );

  // Fetch pending commissions
  const { data: pendingData, refetch: refetchPending } = useQuery(
    ['pending-commissions', referralId],
    () => client.commissions.getPendingCommissions(),
    {
      enabled: !!referralId && open,
      staleTime: 1000 * 30,
    }
  );

  // Fetch claimed commissions
  const { data: claimedData } = useQuery(
    ['claimed-commissions', referralId],
    () => client.commissions.getClaimedCommissions(),
    {
      enabled: !!referralId && open,
      staleTime: 1000 * 30,
    }
  );

  // Claim commission mutation
  const claimMutation = useMutation(
    (commissionData) => client.commissions.claimCommission(commissionData),
    {
      onMutate: (variables) => {
        const sourceId = variables.commission_data?.source_id || variables.commission_id;
        setClaimingIds(prev => new Set([...prev, sourceId]));
      },
      onSuccess: (response, variables) => {
        const commissionAmount = 
          response?.data?.balance_update?.commission_amount ||
          response?.balance_update?.commission_amount ||
          response?.data?.commission?.commission_amount ||
          response?.commission?.commission_amount ||
          variables?.commission_data?.commission_amount ||
          0;
        
        setSnackbar({
          open: true,
          message: `Commission of ${fmtMoney(commissionAmount)} claimed!`,
          severity: 'success'
        });
        
        refetchEligible();
        refetchPending();
        refetch();
        
        queryClient.invalidateQueries({ queryKey: ['referral-detail'] });
        queryClient.invalidateQueries({ queryKey: ['referral-eligible-commissions'] });
        queryClient.invalidateQueries({ queryKey: ['pending-commissions'] });
        queryClient.invalidateQueries({ queryKey: ['claimed-commissions'] });
        queryClient.invalidateQueries({ queryKey: ['ref-activity'] });
        queryClient.invalidateQueries({ queryKey: ['eligible-commissions'] });
        queryClient.invalidateQueries({ queryKey: ['commission-summary'] });
        
        queryClient.refetchQueries({ 
          queryKey: ['claimed-commissions'],
          type: 'active' 
        });
        queryClient.refetchQueries({ 
          queryKey: ['pending-commissions'],
          type: 'active' 
        });
        queryClient.refetchQueries({ 
          queryKey: ['eligible-commissions'],
          type: 'active' 
        });
      },
      onError: (error) => {
        setSnackbar({
          open: true,
          message: error?.response?.data?.error || 'Failed to claim commission',
          severity: 'error'
        });
      },
      onSettled: (data, error, variables) => {
        const sourceId = variables.commission_data?.source_id || variables.commission_id;
        setClaimingIds(prev => {
          const next = new Set(prev);
          next.delete(sourceId);
          return next;
        });
      }
    }
  );

  // Data extraction
  const payload = data?.data ?? data ?? {};
  const refObj = payload.referral || {};
  const activity = payload.activity || {};
  const summary = payload.summary || {};
  const endpoint = payload.endpoint || null;

  // Extract commission rate
  const accessMeta = refObj?.access || {};
  const hasAccess = !!accessMeta?.has_commission_rights;
  const isAllAccess = !!accessMeta?.is_all_access;

  let commissionBps = 0;
  if (accessMeta?.commission_percentage_bps) {
    commissionBps = accessMeta.commission_percentage_bps;
  } else if (refObj?.commission_percentage_bps) {
    commissionBps = refObj.commission_percentage_bps;
  } else if (summary?.potential_commission && summary?.total_value) {
    const totalValue = Number(summary.total_value || 0);
    const potentialCommission = Number(summary.potential_commission || 0);
    if (totalValue > 0 && potentialCommission > 0) {
      commissionBps = Math.round((potentialCommission / totalValue) * 10000);
    }
  }

  const commissionRate = commissionBps / 10000;
  const commissionPercentDisplay = (commissionRate * 100).toFixed(2);

  // User type identification
  const isCreatorEndUser = Boolean(endpoint?.linked_creator);

  // Financial data - SORT BY MOST RECENT FIRST
  const endInvoices = (activity?.end_user?.invoices || []).sort((a, b) => {
    const dateA = new Date(a.created_at || 0);
    const dateB = new Date(b.created_at || 0);
    return dateB - dateA;
  });
  
  const endPayouts = (activity?.end_user?.payouts || []).sort((a, b) => {
    const dateA = new Date(a.payout_date || a.created_at || 0);
    const dateB = new Date(b.payout_date || b.created_at || 0);
    return dateB - dateA;
  });
  
  const endInvoiceSum = Number(activity?.end_user?.total_invoice_amount || 0);
  const endPayoutSum = Number(activity?.end_user?.total_payout_amount || 0);

  const eligibleCommissions = eligibleData?.data?.eligible_commissions || eligibleData?.eligible_commissions || [];
  
  const pendingCommissions = (pendingData?.data?.pending_commissions || [])
    .filter(pc => pc.referral_id === referralId);

  const claimedCommissions = (claimedData?.data?.claimed_commissions || [])
    .filter(cc => cc.referral_id === referralId);

  const totalClaimable = eligibleCommissions.reduce((sum, ec) => sum + Number(ec.commission_amount), 0);

  // Title logic
  const titleName = (() => {
    const f = refObj.first_name, l = refObj.last_name;
    if (f || l) return `${f || ''} ${l || ''}`.trim();
    if (endpoint?.linked_user) {
      const ef = endpoint.linked_user.first_name || '';
      const el = endpoint.linked_user.last_name || '';
      if (ef || el) return `${ef} ${el}`.trim();
    }
    if (endpoint?.linked_creator?.username) return endpoint.linked_creator.username;
    return refObj.email || `Referral #${refObj.id || ''}`.trim();
  })();

  // Commission status logic
  const getInvoiceCommissionStatus = (invoiceId) => {
    const invoiceIdStr = String(invoiceId);
    
    const pending = pendingCommissions.find(pc => String(pc.source_id) === invoiceIdStr);
    if (pending) return { status: 'pending', commission: pending };
    
    const eligible = eligibleCommissions.find(ec => String(ec.source_id) === invoiceIdStr);
    if (eligible) return { status: 'claimable', commission: eligible };
    
    const claimed = claimedCommissions.find(cc => String(cc.source_id) === invoiceIdStr);
    if (claimed) return { status: 'claimed', commission: claimed };
    
    return { status: 'not_eligible', commission: null };
  };

  // Handle claim commission
  const handleClaimCommission = (commissionData) => {
    claimMutation.mutate({ commission_data: commissionData });
  };

  // Handle claim all
  const handleClaimAll = () => {
    if (eligibleCommissions.length === 0) return;
    eligibleCommissions.forEach(commission => {
      handleClaimCommission(commission);
    });
  };

  if (!open) return null;

  // Summary Card Component (for desktop left column)
  const SummaryCard = () => (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        borderRadius: 3,
        background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
        color: 'white',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Avatar
          sx={{
            width: 64,
            height: 64,
            bgcolor: alpha('#fff', 0.2),
            fontSize: '1.75rem',
            fontWeight: 700,
          }}
        >
          {titleName.charAt(0).toUpperCase()}
        </Avatar>
        <Box flex={1} minWidth={0}>
          <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }} noWrap>
            {titleName}
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
            {isAllAccess && (
              <Chip
                icon={<PublicIcon sx={{ fontSize: 14 }} />}
                label="All-Access"
                size="small"
                sx={{ 
                  bgcolor: alpha('#fff', 0.2),
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  height: 22,
                }}
              />
            )}
            {hasAccess && !isAllAccess && (
              <Chip
                icon={<LockIcon sx={{ fontSize: 14 }} />}
                label="Partner"
                size="small"
                sx={{ 
                  bgcolor: alpha('#fff', 0.2),
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  height: 22,
                }}
              />
            )}
            {refObj?.status && (
              <Chip
                icon={refObj.status === 'completed' ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : undefined}
                label={refObj.status}
                size="small"
                sx={{ 
                  bgcolor: alpha('#fff', 0.2),
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  height: 22,
                  textTransform: 'capitalize',
                }}
              />
            )}
          </Stack>
        </Box>
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6}>
          <Box sx={{ p: 2, bgcolor: alpha('#fff', 0.1), borderRadius: 2 }}>
            <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
              Invoices
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {hasAccess ? endInvoices.length : '—'}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ p: 2, bgcolor: alpha('#fff', 0.1), borderRadius: 2 }}>
            <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
              {isCreatorEndUser ? 'Payouts' : 'Total Value'}
            </Typography>
            <Typography variant="h4" fontWeight="bold" noWrap>
              {hasAccess ? (isCreatorEndUser ? endPayouts.length : fmtMoney(endInvoiceSum)) : '—'}
            </Typography>
          </Box>
        </Grid>
        {hasAccess && commissionBps > 0 && (
          <Grid item xs={12}>
            <Box sx={{ p: 2, bgcolor: alpha('#fff', 0.15), borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                Commission Rate
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <PercentIcon sx={{ fontSize: 28 }} />
                <Typography variant="h3" fontWeight={900}>
                  {commissionPercentDisplay}%
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 600, mt: 0.5, display: 'block' }}>
                per paid invoice
              </Typography>
            </Box>
          </Grid>
        )}
        <Grid item xs={12}>
          <Box sx={{ p: 2.5, bgcolor: alpha('#10b981', 0.2), borderRadius: 2, textAlign: 'center', border: '2px solid', borderColor: alpha('#10b981', 0.3) }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
              Your Total Earnings
            </Typography>
            <Typography variant="h3" fontWeight={900} noWrap>
              {hasAccess && summary.potential_commission != null ? fmtMoney(summary.potential_commission) : '—'}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Claim All Button */}
      {hasAccess && totalClaimable > 0 && (
        <Button
          variant="contained"
          size="large"
          fullWidth
          startIcon={<WalletIcon />}
          onClick={handleClaimAll}
          disabled={claimMutation.isLoading || eligibleCommissions.length === 0}
          sx={{
            bgcolor: 'white',
            color: '#6366f1',
            fontWeight: 700,
            py: 1.5,
            fontSize: '1rem',
            '&:hover': {
              bgcolor: alpha('#fff', 0.9),
            },
            '&:disabled': {
              bgcolor: alpha('#fff', 0.5),
              color: alpha('#6366f1', 0.5),
            }
          }}
        >
          Claim All ({eligibleCommissions.length}) • {fmtMoney(totalClaimable)}
        </Button>
      )}
    </Paper>
  );

  // Mobile View
  if (isMobile) {
    return (
      <>
        <Dialog 
          open={open} 
          onClose={onClose} 
          fullWidth 
          maxWidth="xl"
          fullScreen
          PaperProps={{
            elevation: 0,
            sx: {
              borderRadius: 0,
              boxShadow: 'none',
              m: 0,
              paddingTop: 'env(safe-area-inset-top, 0px)',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              height: '100vh',
              maxHeight: '100vh',
            },
          }}
        >
          {/* Compact Header */}
          <Box sx={{ 
            p: 2,
            background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
            color: 'white',
          }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: hasAccess && totalClaimable > 0 ? 2 : 0 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  minWidth: 48,
                  borderRadius: 2,
                  bgcolor: alpha('#fff', 0.2),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                }}
              >
                {titleName.charAt(0).toUpperCase()}
              </Box>
              
              <Box flex={1} minWidth={0}>
                <Typography variant={isSmallMobile ? "subtitle1" : "h6"} fontWeight={800} sx={{ mb: 0.5, lineHeight: 1.2 }} noWrap>
                  {titleName}
                </Typography>
                
                <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                  {isAllAccess && (
                    <Chip
                      icon={<PublicIcon sx={{ fontSize: 12 }} />}
                      label="All-Access"
                      size="small"
                      sx={{ 
                        bgcolor: alpha('#fff', 0.2),
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        height: 20,
                      }}
                    />
                  )}
                  {hasAccess && !isAllAccess && (
                    <Chip
                      icon={<LockIcon sx={{ fontSize: 12 }} />}
                      label="Partner"
                      size="small"
                      sx={{ 
                        bgcolor: alpha('#fff', 0.2),
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        height: 20,
                      }}
                    />
                  )}
                  {refObj?.status && (
                    <Chip
                      icon={refObj.status === 'completed' ? <CheckCircleIcon sx={{ fontSize: 12 }} /> : undefined}
                      label={refObj.status}
                      size="small"
                      sx={{ 
                        bgcolor: alpha('#fff', 0.2),
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        height: 20,
                        textTransform: 'capitalize',
                      }}
                    />
                  )}
                </Stack>
              </Box>

              <IconButton
                onClick={onClose}
                size="small"
                sx={{
                  color: 'white',
                  bgcolor: alpha('#fff', 0.1),
                  '&:hover': { bgcolor: alpha('#fff', 0.2) },
                  width: 36,
                  height: 36,
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>

            {/* Claim All Button */}
            {hasAccess && totalClaimable > 0 && (
              <Button
                variant="contained"
                size="medium"
                fullWidth
                startIcon={<WalletIcon sx={{ fontSize: 18 }} />}
                onClick={handleClaimAll}
                disabled={claimMutation.isLoading || eligibleCommissions.length === 0}
                sx={{
                  bgcolor: 'white',
                  color: '#6366f1',
                  fontWeight: 700,
                  py: 1.25,
                  fontSize: '0.875rem',
                  '&:hover': {
                    bgcolor: alpha('#fff', 0.9),
                  },
                  '&:disabled': {
                    bgcolor: alpha('#fff', 0.5),
                    color: alpha('#6366f1', 0.5),
                  }
                }}
              >
                Claim All ({eligibleCommissions.length}) • {fmtMoney(totalClaimable)}
              </Button>
            )}
          </Box>

          <Box sx={{ 
            px: 2, 
            overflowY: 'auto', 
            flex: 1, 
            WebkitOverflowScrolling: 'touch', 
            pb: 2,
          }}>
            {isLoading ? (
              <Box py={4}><LinearProgress /></Box>
            ) : isError ? (
              <Alert severity="error" sx={{ borderRadius: 2, mt: 2 }}>Failed to load referral details.</Alert>
            ) : (
              <Stack spacing={2} sx={{ mt: 2 }}>
                {/* Stats Grid - Fixed Layout */}
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 1.5,
                        borderRadius: 2,
                        textAlign: 'center',
                        border: '2px solid',
                        borderColor: alpha('#6366f1', 0.1),
                        bgcolor: alpha('#6366f1', 0.02),
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        minHeight: 80,
                      }}
                    >
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ 
                          fontWeight: 700, 
                          textTransform: 'uppercase', 
                          fontSize: '0.65rem',
                          mb: 0.5,
                          display: 'block',
                        }}
                      >
                        Invoices
                      </Typography>
                      <Typography 
                        variant="h5" 
                        fontWeight="bold" 
                        color="primary.main"
                        sx={{ lineHeight: 1 }}
                      >
                        {hasAccess ? endInvoices.length : '—'}
                      </Typography>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 1.5,
                        borderRadius: 2,
                        textAlign: 'center',
                        border: '2px solid',
                        borderColor: alpha(isCreatorEndUser ? '#10b981' : '#f59e0b', 0.1),
                        bgcolor: alpha(isCreatorEndUser ? '#10b981' : '#f59e0b', 0.02),
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        minHeight: 80,
                      }}
                    >
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ 
                          fontWeight: 700, 
                          textTransform: 'uppercase', 
                          fontSize: '0.65rem',
                          mb: 0.5,
                          display: 'block',
                        }}
                      >
                        {isCreatorEndUser ? 'Payouts' : 'Total Value'}
                      </Typography>
                      <Typography 
                        variant="h5" 
                        fontWeight="bold" 
                        sx={{ 
                          color: isCreatorEndUser ? '#10b981' : '#d97706',
                          fontSize: isSmallMobile ? '1.15rem' : '1.5rem',
                          lineHeight: 1,
                        }}
                        noWrap
                      >
                        {hasAccess ? (isCreatorEndUser ? endPayouts.length : fmtMoney(endInvoiceSum)) : '—'}
                      </Typography>
                    </Paper>
                  </Grid>
                  
                  {hasAccess && commissionBps > 0 && (
                    <Grid item xs={6}>
                      <Paper 
                        elevation={0}
                        sx={{ 
                          p: 1.5,
                          borderRadius: 2,
                          textAlign: 'center',
                          border: '2px solid',
                          borderColor: alpha('#8b5cf6', 0.2),
                          bgcolor: alpha('#8b5cf6', 0.05),
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          minHeight: 80,
                        }}
                      >
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          sx={{ 
                            fontWeight: 700, 
                            textTransform: 'uppercase', 
                            fontSize: '0.65rem',
                            mb: 0.5,
                            display: 'block',
                          }}
                        >
                          Commission
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
                          <PercentIcon sx={{ fontSize: 16, color: '#7c3aed' }} />
                          <Typography 
                            variant="h5" 
                            fontWeight="bold" 
                            sx={{ 
                              color: '#7c3aed',
                              fontSize: isSmallMobile ? '1.15rem' : '1.5rem',
                              lineHeight: 1,
                            }}
                          >
                            {commissionPercentDisplay}%
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  )}
                  
                  <Grid item xs={hasAccess && commissionBps > 0 ? 6 : 12}>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 1.5,
                        borderRadius: 2,
                        textAlign: 'center',
                        border: '2px solid',
                        borderColor: alpha('#10b981', 0.2),
                        bgcolor: alpha('#10b981', 0.05),
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        minHeight: 80,
                      }}
                    >
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ 
                          fontWeight: 700, 
                          textTransform: 'uppercase', 
                          fontSize: '0.65rem',
                          mb: 0.5,
                          display: 'block',
                        }}
                      >
                        Your Earnings
                      </Typography>
                      <Typography 
                        variant="h5" 
                        fontWeight="bold" 
                        color="success.main" 
                        sx={{ 
                          fontSize: isSmallMobile ? '1.15rem' : '1.5rem',
                          lineHeight: 1,
                        }}
                        noWrap
                      >
                        {hasAccess && summary.potential_commission != null ? fmtMoney(summary.potential_commission) : '—'}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Basic Info */}
                <CollapsibleSection
                  title="Basic Information"
                  icon={<PersonIcon />}
                  defaultExpanded={true}
                  isMobile={true}
                >
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <InfoRow
                          label="Email"
                          value={refObj.email || '—'}
                          icon={<EmailIcon />}
                          isMobile={true}
                        />
                        <InfoRow
                          label="Type"
                          value={refObj.referral_type || '—'}
                          icon={<BusinessIcon />}
                          isMobile={true}
                        />
                        <InfoRow
                          label="Created"
                          value={fmtDate(refObj.date_generated)}
                          icon={<CalendarIcon />}
                          isMobile={true}
                        />
                        <InfoRow
                          label="Completed"
                          value={fmtDate(refObj.date_completed)}
                          icon={<CheckCircleIcon />}
                          isMobile={true}
                        />
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CollapsibleSection>

                {/* Access Denied or Activity Sections */}
                {!hasAccess ? (
                  <Alert
                    severity="info"
                    icon={<LockIcon />}
                    sx={{ borderRadius: 2 }}
                  >
                    <Typography fontWeight={600} gutterBottom sx={{ fontSize: '0.875rem' }}>
                      Partner Access Required
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      You need partner access to view detailed financial activity and claim commissions.
                    </Typography>
                  </Alert>
                ) : (
                  <>
                    {/* Invoices Section */}
                    {endInvoices.length > 0 && (
                      <CollapsibleSection
                        title={`Invoices${isCreatorEndUser ? ' (Sent)' : ''} (${endInvoices.length})`}
                        icon={<ReceiptIcon />}
                        defaultExpanded={true}
                        isMobile={true}
                      >
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 1.5 }}>
                          Earn {commissionPercentDisplay}% on paid invoices • {eligibleCommissions.length} claimable
                        </Typography>

                        {eligibleLoading && (
                          <Box py={2}>
                            <LinearProgress />
                          </Box>
                        )}

                        <Box sx={{ overflowX: 'auto', mx: -1.5 }}>
                          <Table size="small" sx={{ minWidth: isSmallMobile ? 340 : 400 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', whiteSpace: 'nowrap', px: 1 }}>Date</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', whiteSpace: 'nowrap', px: 1 }}>Amount</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', px: 1 }}>Commission</TableCell>
                                {!isSmallMobile && (
                                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', px: 1 }}>Status</TableCell>
                                )}
                                <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.7rem', px: 1 }}>Action</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {endInvoices.map((invoice, idx) => {
                                const amount = Number(invoice.amount_due ?? invoice.amount);
                                const commissionAmount = amount * commissionRate;
                                const isPaid = invoice.status === 'paid';
                                const commissionStatus = getInvoiceCommissionStatus(invoice.id);
                                const isClaiming = claimingIds.has(String(invoice.id));

                                return (
                                  <TableRow 
                                    key={invoice.id || idx}
                                    sx={{ 
                                      bgcolor: commissionStatus.status === 'claimable' 
                                        ? alpha('#10b981', 0.05) 
                                        : 'transparent',
                                    }}
                                  >
                                    <TableCell sx={{ px: 1, py: 1.25 }}>
                                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                                        {fmtDate(invoice.created_at)}
                                      </Typography>
                                    </TableCell>
                                    
                                    <TableCell align="right" sx={{ px: 1, py: 1.25 }}>
                                      <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                        {fmtMoney(amount)}
                                      </Typography>
                                    </TableCell>
                                    
                                    <TableCell align="right" sx={{ px: 1, py: 1.25 }}>
                                      <Typography 
                                        variant="body2"
                                        fontWeight={800}
                                        color={isPaid && commissionStatus.status === 'claimable' ? 'success.main' : 'text.secondary'}
                                        sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                                      >
                                        {fmtMoney(commissionAmount)}
                                      </Typography>
                                    </TableCell>
                                    
                                    {!isSmallMobile && (
                                      <TableCell sx={{ px: 1, py: 1.25 }}>
                                        <Chip 
                                          size="small" 
                                          label={invoice.status || 'pending'}
                                          color={isPaid ? 'success' : 'default'}
                                          sx={{ 
                                            fontWeight: 700,
                                            fontSize: '0.6rem',
                                            height: 18,
                                            textTransform: 'capitalize',
                                          }}
                                        />
                                      </TableCell>
                                    )}
                                    
                                    <TableCell align="center" sx={{ px: 1, py: 1.25 }}>
                                      {commissionStatus.status === 'claimable' && isPaid && (
                                        <Button
                                          size="small"
                                          variant="contained"
                                          startIcon={isClaiming ? <CircularProgress size={10} /> : (!isSmallMobile && <WalletIcon sx={{ fontSize: 12 }} />)}
                                          disabled={isClaiming || claimMutation.isLoading}
                                          onClick={() => handleClaimCommission({
                                            referral_id: referralId,
                                            source_type: 'invoice',
                                            source_id: String(invoice.id),
                                            source_amount: amount,
                                            commission_amount: commissionAmount,
                                            commission_rate: commissionRate,
                                            commission_type: 'general',
                                            status: 'pending',
                                            meta_data: {
                                              invoice_id: invoice.id,
                                              invoice_status: invoice.status,
                                              invoice_date: invoice.created_at,
                                            }
                                          })}
                                          sx={{
                                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                            fontWeight: 700,
                                            fontSize: '0.6rem',
                                            px: 1,
                                            py: 0.5,
                                            minWidth: 50,
                                          }}
                                        >
                                          {isClaiming ? '...' : 'Claim'}
                                        </Button>
                                      )}
                                      
                                      {commissionStatus.status === 'pending' && (
                                        <Chip 
                                          size="small"
                                          label="Processing"
                                          color="warning"
                                          sx={{ fontWeight: 700, fontSize: '0.55rem', height: 18 }}
                                        />
                                      )}
                                      
                                      {commissionStatus.status === 'claimed' && (
                                        <Chip 
                                          size="small"
                                          label="Claimed"
                                          icon={<CheckCircleIcon sx={{ fontSize: 10 }} />}
                                          color="success"
                                          sx={{ fontWeight: 700, fontSize: '0.55rem', height: 18 }}
                                        />
                                      )}
                                      
                                      {commissionStatus.status === 'not_eligible' && (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                          {isPaid ? 'N/A' : 'Unpaid'}
                                        </Typography>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </Box>
                      </CollapsibleSection>
                    )}

                    {/* Payouts Section */}
                    {isCreatorEndUser && endPayouts.length > 0 && (
                      <CollapsibleSection
                        title={`Payouts (${endPayouts.length})`}
                        icon={<PaymentIcon />}
                        defaultExpanded={false}
                        isMobile={true}
                      >
                        <Box sx={{ overflowX: 'auto', mx: -1.5 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', px: 1 }}>Date</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', px: 1 }}>Amount</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', px: 1 }}>Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {endPayouts.slice(0, 10).map((p, idx) => (
                                <TableRow key={p.id || idx}>
                                  <TableCell sx={{ px: 1, py: 1.25 }}>
                                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.7rem' }}>
                                      {fmtDate(p.payout_date || p.created_at)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right" sx={{ px: 1, py: 1.25 }}>
                                    <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.75rem' }}>
                                      {fmtMoney(p.amount)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ px: 1, py: 1.25 }}>
                                    <Chip 
                                      size="small" 
                                      label={p.status || 'completed'} 
                                      color={p.status === 'paid' || p.status === 'completed' ? 'success' : 'default'}
                                      sx={{ fontWeight: 700, fontSize: '0.6rem', height: 18 }}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </CollapsibleSection>
                    )}
                  </>
                )}
              </Stack>
            )}
          </Box>

          <Box sx={{ 
            px: 2, 
            pb: 2,
            bgcolor: 'background.paper', 
            borderTop: '1px solid', 
            borderColor: 'divider',
            paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
          }}>
            <Button 
              onClick={onClose} 
              variant="outlined" 
              size="large"
              fullWidth
              sx={{ 
                fontSize: '0.875rem', 
                textTransform: 'none', 
                fontWeight: 600,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                }
              }}
            >
              Close
            </Button>
          </Box>
        </Dialog>

        {/* Snackbar */}
        {snackbar.open && (
          <Box
            sx={{
              position: 'fixed',
              bottom: APP_SHELL_HEIGHT + EXTRA_GUTTER + 10,
              left: 16,
              right: 16,
              zIndex: 9999,
            }}
          >
            <Alert 
              severity={snackbar.severity}
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              sx={{ borderRadius: 2, fontWeight: 600, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', fontSize: '0.875rem' }}
            >
              {snackbar.message}
            </Alert>
          </Box>
        )}
      </>
    );
  }

  // Desktop View - Full Screen Overlay
  return (
    <>
      <Fade in={open} timeout={300}>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'background.default',
            zIndex: theme.zIndex.modal + 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header Bar */}
          <Box
            sx={{
              height: 80,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 4,
              bgcolor: 'background.paper',
              backdropFilter: 'blur(20px)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2.5,
                  background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                }}
              >
                {titleName.charAt(0).toUpperCase()}
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  {titleName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Referral #{refObj.id} • {refObj.email}
                </Typography>
              </Box>
            </Box>

            <IconButton 
              onClick={onClose}
              sx={{
                bgcolor: 'action.hover',
                '&:hover': { bgcolor: 'action.selected' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Main Content - Two Column Layout */}
          <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            {/* Left Column - Summary */}
            <Box
              sx={{
                width: 420,
                borderRight: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.paper',
                overflowY: 'auto',
              }}
            >
              <Box sx={{ p: 4 }}>
                {isLoading ? (
                  <Box py={4}><LinearProgress /></Box>
                ) : isError ? (
                  <Alert severity="error">Failed to load referral details.</Alert>
                ) : (
                  <SummaryCard />
                )}
              </Box>
            </Box>

            {/* Right Column - Details */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 4 }}>
              {isLoading ? (
                <Box py={4}><LinearProgress /></Box>
              ) : isError ? (
                <Alert severity="error">Failed to load referral details.</Alert>
              ) : (
                <Stack spacing={3}>
                  {/* Basic Info */}
                  <CollapsibleSection
                    title="Basic Information"
                    icon={<PersonIcon />}
                    defaultExpanded={true}
                    isMobile={false}
                  >
                    <TableContainer>
                      <Table size="small">
                        <TableBody>
                          <InfoRow
                            label="Email"
                            value={refObj.email || '—'}
                            icon={<EmailIcon />}
                            isMobile={false}
                          />
                          <InfoRow
                            label="Type"
                            value={refObj.referral_type || '—'}
                            icon={<BusinessIcon />}
                            isMobile={false}
                          />
                          <InfoRow
                            label="Created"
                            value={fmtDate(refObj.date_generated)}
                            icon={<CalendarIcon />}
                            isMobile={false}
                          />
                          <InfoRow
                            label="Completed"
                            value={fmtDate(refObj.date_completed)}
                            icon={<CheckCircleIcon />}
                            isMobile={false}
                          />
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CollapsibleSection>

                  {/* Access Denied or Activity Sections */}
                  {!hasAccess ? (
                    <Alert severity="info" icon={<LockIcon />} sx={{ borderRadius: 2 }}>
                      <Typography fontWeight={600} gutterBottom>
                        Partner Access Required
                      </Typography>
                      <Typography variant="body2">
                        You need partner access to view detailed financial activity and claim commissions.
                      </Typography>
                    </Alert>
                  ) : (
                    <>
                      {/* Invoices Section */}
                      {endInvoices.length > 0 && (
                        <CollapsibleSection
                          title={`Invoices${isCreatorEndUser ? ' (Sent)' : ''} (${endInvoices.length})`}
                          icon={<ReceiptIcon />}
                          defaultExpanded={true}
                          isMobile={false}
                        >
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Earn {commissionPercentDisplay}% on paid invoices • {eligibleCommissions.length} claimable • Total: {fmtMoney(endInvoiceSum)}
                          </Typography>

                          {eligibleLoading && (
                            <Box py={2}>
                              <LinearProgress />
                            </Box>
                          )}

                          <TableContainer component={Paper} variant="outlined">
                            <Table size="medium">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700 }}>Your {commissionPercentDisplay}%</TableCell>
                                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 700 }}>Action</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {endInvoices.map((invoice, idx) => {
                                  const amount = Number(invoice.amount_due ?? invoice.amount);
                                  const commissionAmount = amount * commissionRate;
                                  const isPaid = invoice.status === 'paid';
                                  const commissionStatus = getInvoiceCommissionStatus(invoice.id);
                                  const isClaiming = claimingIds.has(String(invoice.id));

                                  return (
                                    <TableRow 
                                      key={invoice.id || idx}
                                      sx={{ 
                                        bgcolor: commissionStatus.status === 'claimable' 
                                          ? alpha('#10b981', 0.05) 
                                          : 'transparent',
                                        '&:hover': {
                                          bgcolor: commissionStatus.status === 'claimable'
                                            ? alpha('#10b981', 0.1)
                                            : alpha('#000', 0.02)
                                        }
                                      }}
                                    >
                                      <TableCell>
                                        <Typography variant="body2" fontWeight={600}>
                                          {fmtDate(invoice.created_at)}
                                        </Typography>
                                      </TableCell>
                                      
                                      <TableCell align="right">
                                        <Typography variant="body2" fontWeight={700}>
                                          {fmtMoney(amount)}
                                        </Typography>
                                      </TableCell>
                                      
                                      <TableCell align="right">
                                        <Typography 
                                          variant="h6"
                                          fontWeight={800}
                                          color={isPaid && commissionStatus.status === 'claimable' ? 'success.main' : 'text.secondary'}
                                        >
                                          {fmtMoney(commissionAmount)}
                                        </Typography>
                                      </TableCell>
                                      
                                      <TableCell>
                                        <Chip 
                                          size="small" 
                                          label={invoice.status || 'pending'}
                                          color={isPaid ? 'success' : 'default'}
                                          sx={{ fontWeight: 700, textTransform: 'capitalize' }}
                                        />
                                      </TableCell>
                                      
                                      <TableCell align="center">
                                        {commissionStatus.status === 'claimable' && isPaid && (
                                          <Button
                                            size="small"
                                            variant="contained"
                                            startIcon={isClaiming ? <CircularProgress size={14} /> : <WalletIcon sx={{ fontSize: 16 }} />}
                                            disabled={isClaiming || claimMutation.isLoading}
                                            onClick={() => handleClaimCommission({
                                              referral_id: referralId,
                                              source_type: 'invoice',
                                              source_id: String(invoice.id),
                                              source_amount: amount,
                                              commission_amount: commissionAmount,
                                              commission_rate: commissionRate,
                                              commission_type: 'general',
                                              status: 'pending',
                                              meta_data: {
                                                invoice_id: invoice.id,
                                                invoice_status: invoice.status,
                                                invoice_date: invoice.created_at,
                                              }
                                            })}
                                            sx={{
                                              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                              fontWeight: 700,
                                              px: 2.5,
                                              py: 0.75,
                                            }}
                                          >
                                            {isClaiming ? '...' : 'Claim'}
                                          </Button>
                                        )}
                                        
                                        {commissionStatus.status === 'pending' && (
                                          <Chip 
                                            size="small"
                                            label="Processing"
                                            color="warning"
                                            sx={{ fontWeight: 700 }}
                                          />
                                        )}
                                        
                                        {commissionStatus.status === 'claimed' && (
                                          <Chip 
                                            size="small"
                                            label="Claimed"
                                            icon={<CheckCircleIcon />}
                                            color="success"
                                            sx={{ fontWeight: 700 }}
                                          />
                                        )}
                                        
                                        {commissionStatus.status === 'not_eligible' && (
                                          <Typography variant="caption" color="text.secondary">
                                            {isPaid ? 'N/A' : 'Unpaid'}
                                          </Typography>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CollapsibleSection>
                      )}

                      {/* Payouts Section */}
                      {isCreatorEndUser && endPayouts.length > 0 && (
                        <CollapsibleSection
                          title={`Payouts (${endPayouts.length})`}
                          icon={<PaymentIcon />}
                          defaultExpanded={false}
                          isMobile={false}
                        >
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Total payout value: {fmtMoney(endPayoutSum)}
                          </Typography>

                          <TableContainer component={Paper} variant="outlined">
                            <Table size="medium">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {endPayouts.slice(0, 10).map((p, idx) => (
                                  <TableRow key={p.id || idx}>
                                    <TableCell>
                                      <Typography variant="body2" fontWeight={600}>
                                        {fmtDate(p.payout_date || p.created_at)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="body2" fontWeight={700}>
                                        {fmtMoney(p.amount)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Chip 
                                        size="small" 
                                        label={p.status || 'completed'} 
                                        color={p.status === 'paid' || p.status === 'completed' ? 'success' : 'default'}
                                        sx={{ fontWeight: 700 }}
                                      />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CollapsibleSection>
                      )}
                    </>
                  )}

                  {/* Action Buttons - Sticky at bottom */}
                  <Box 
                    sx={{ 
                      position: 'sticky',
                      bottom: 0,
                      bgcolor: 'background.default',
                      pt: 3,
                      pb: 2,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      mt: 4,
                    }}
                  >
                    <Button 
                      onClick={onClose} 
                      variant="contained" 
                      size="large"
                      fullWidth
                      sx={{ px: 4, textTransform: 'none', fontWeight: 600 }}
                    >
                      Close
                    </Button>
                  </Box>
                </Stack>
              )}
            </Box>
          </Box>
        </Box>
      </Fade>

      {/* Snackbar for desktop */}
      {snackbar.open && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: theme.zIndex.modal + 2,
          }}
        >
          <Alert 
            severity={snackbar.severity}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            sx={{ 
              borderRadius: 2,
              fontWeight: 600,
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            }}
          >
            {snackbar.message}
          </Alert>
        </Box>
      )}
    </>
  );
}